'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    Anamnesis,
    deleteAnamnesis,
    getAnamnesesByClient,
    MEDICAL_SPECIALTY_LABELS,
} from '@/lib/api/prontuario/prontuario-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdAssignment,
    MdDelete,
    MdEdit,
    MdPerson,
    MdVisibility,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData, useProntuarioNavigate } from './prontuario-context'

export default function AnamnesesPage() {
    const navigate = useProntuarioNavigate()
    const { client, clientId } = useProntuarioData()

    const [anamneses, setAnamneses] = useState<Anamnesis[]>([])
    const [loading, setLoading] = useState(true)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [anamnesisToDelete, setAnamnesisToDelete] = useState<number | null>(
        null
    )

    const basePath = `/person/clients/${clientId}/prontuario`

    useEffect(() => {
        if (clientId) {
            loadAnamneses()
        }
    }, [clientId])

    const loadAnamneses = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getAnamnesesByClient(parseInt(clientId, 10))
            if (response.success) {
                setAnamneses(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar anamneses')
            }
        } catch (error) {
            toast.error('Erro ao carregar anamneses')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setAnamnesisToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!anamnesisToDelete) return

        try {
            const response = await deleteAnamnesis(anamnesisToDelete)
            if (response.success) {
                toast.success('Anamnese excluída com sucesso!')
                await loadAnamneses()
            } else {
                toast.error(response.error || 'Erro ao excluir anamnese')
            }
        } catch (error) {
            toast.error('Erro ao excluir anamnese')
        } finally {
            setShowDeleteModal(false)
            setAnamnesisToDelete(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando anamneses...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        Anamneses
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Histórico de anamneses do paciente
                    </p>
                </div>
                <button
                    onClick={() => navigate(`${basePath}/anamneses/new`)}
                    className="btn btn-secondary gap-2"
                >
                    <MdAdd className="text-lg" />
                    Nova Anamnese
                </button>
            </div>

            {/* Anamneses List */}
            {anamneses.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdAssignment className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">
                        Nenhuma anamnese registrada
                    </p>
                    <p className="text-sm text-base-content/40 mt-1">
                        Clique no botão acima para criar uma anamnese
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {anamneses.map(anamnesis => (
                        <div
                            key={anamnesis.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdAssignment className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-base-content">
                                                    Anamnese #{anamnesis.id}
                                                </h3>
                                                <p className="text-sm text-base-content/60">
                                                    {format(
                                                        new Date(
                                                            anamnesis.createdAt
                                                        ),
                                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                                        { locale: ptBR }
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {anamnesis.template && (
                                                <span className="badge badge-info badge-outline">
                                                    {anamnesis.template.name}
                                                </span>
                                            )}
                                            {anamnesis.template?.specialty && (
                                                <span className="badge badge-ghost">
                                                    {
                                                        MEDICAL_SPECIALTY_LABELS[
                                                            anamnesis.template
                                                                .specialty
                                                        ]
                                                    }
                                                </span>
                                            )}
                                        </div>

                                        {anamnesis.chiefComplaint && (
                                            <div className="mb-3">
                                                <p className="text-xs font-semibold text-base-content/60 mb-1">
                                                    Queixa Principal:
                                                </p>
                                                <p className="text-sm text-base-content line-clamp-2">
                                                    {anamnesis.chiefComplaint}
                                                </p>
                                            </div>
                                        )}

                                        {anamnesis.diagnosticHypothesis && (
                                            <div className="mb-3">
                                                <p className="text-xs font-semibold text-base-content/60 mb-1">
                                                    Hipótese Diagnóstica:
                                                </p>
                                                <p className="text-sm text-base-content line-clamp-2">
                                                    {
                                                        anamnesis.diagnosticHypothesis
                                                    }
                                                </p>
                                            </div>
                                        )}

                                        {anamnesis.professional && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/60 mt-3 pt-3 border-t border-base-300/50">
                                                <MdPerson className="text-base" />
                                                <span>
                                                    {
                                                        anamnesis.professional
                                                            .name
                                                    }
                                                    {anamnesis.professional
                                                        .registrationNumber && (
                                                        <>
                                                            {' '}
                                                            -{' '}
                                                            {
                                                                anamnesis
                                                                    .professional
                                                                    .councilName
                                                            }{' '}
                                                            {
                                                                anamnesis
                                                                    .professional
                                                                    .registrationNumber
                                                            }
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `${basePath}/anamneses/${anamnesis.id}`
                                                )
                                            }
                                            className="btn btn-ghost btn-sm btn-square"
                                            title="Visualizar"
                                        >
                                            <MdVisibility className="text-lg" />
                                        </button>
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `${basePath}/anamneses/${anamnesis.id}/editar`
                                                )
                                            }
                                            className="btn btn-ghost btn-sm btn-square"
                                            title="Editar"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                        <button
                                            onClick={() =>
                                                handleDelete(anamnesis.id)
                                            }
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                            title="Excluir"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setAnamnesisToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir anamnese"
                message="Tem certeza que deseja excluir esta anamnese?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
